/**
 * Currency Config Thunk
 *
 * Fetches global currency/region/language configuration.
 * Called once in listing, stored for reuse across details/room-selection/review.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';
import { fetchHotelCurrencyConfig, type CurrencyConfigData } from '../api/currencyConfigApi';

// ============================================
// Thunk
// ============================================

export const fetchCurrencyConfigThunk = createAsyncThunk<
  CurrencyConfigData,
  void,
  { state: RootState; rejectValue: string }
>('hotels/config/fetchCurrencyConfig', async (_, { getState, rejectWithValue }) => {
  // Skip if already loaded
  const { currencyConfig } = getState().hotels.config;
  if (currencyConfig !== null) {
    return currencyConfig;
  }

  const sessionId = getOrCreateSessionId();
  const response = await fetchHotelCurrencyConfig({
    sessionId,
    userId: getUUID(),
  });

  if (!response.success || response.data === null) {
    return rejectWithValue(response.error?.message ?? 'Failed to fetch currency config');
  }

  return response.data;
});
